import {
  Box,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  tooltipClasses,
} from '@mui/material';
import { Tooltip, theme } from '@side/pantry';
import { type ReactElement, useMemo } from 'react';
import { useReportContext } from '../context/ReportContext';
import { useGlossaryDefinitions } from './GlossaryButton/useGlossaryDefinitions';

export const GlossaryTooltip = ({
  children,
  label,
  lookupLabel,
  caption,
  darkColor = theme.palette.periwinkle.main,
  lightColor = theme.palette.warning[600],
  sx,
}: {
  children: ReactElement;
  label?: string;
  lookupLabel?: string;
  caption?: string;
  darkColor?: string;
  lightColor?: string;
  sx?: SxProps<Theme>;
}) => {
  const { showInlineGlossaryDefinitions } = useReportContext();
  const glossaryDefinitions = useGlossaryDefinitions();
  const glossaryList = useMemo(
    () => glossaryDefinitions.flatMap(({ items }) => items),
    [glossaryDefinitions],
  );

  if ((!label && !caption) || !showInlineGlossaryDefinitions) {
    return children;
  }

  const resolvedLookupLabel = lookupLabel ?? label;
  const glossaryItem = resolvedLookupLabel
    ? glossaryList.find(
        (item) =>
          item.title === resolvedLookupLabel ||
          item.matches?.includes(resolvedLookupLabel),
      )
    : null;

  if (!glossaryItem && !caption) {
    return children;
  }

  return (
    <Tooltip
      title={
        <Stack sx={{ gap: 0.5, backgroundColor: 'background.paper', p: 3 }}>
          {(glossaryItem?.title ?? label) && (
            <Typography variant="caption">
              {glossaryItem?.title ?? label}
            </Typography>
          )}
          {/* caption takes precedence over glossary definition by design */}
          <Typography variant="body3">
            {caption ?? glossaryItem?.definition}
          </Typography>
        </Stack>
      }
      arrow={false}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [16, 0],
              },
            },
          ],
          sx: {
            [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
              {
                marginTop: '0px',
              },
          },
        },
        tooltip: {
          sx: {
            p: 0.25,
            backgroundColor: 'background.paper',
            color: 'grey.900',
            borderRadius: '2px',
            backgroundImage: `linear-gradient(to right, ${lightColor} 0%, ${darkColor} 60%)`,
          },
        },
      }}
      enterDelay={200}
      enterNextDelay={200}
      placement="bottom-start"
    >
      <Box
        component="span"
        sx={{
          position: 'relative',
          display: 'inline-flex',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '1px',
            height: '2px',
            backgroundImage: `linear-gradient(to right, ${darkColor}, ${lightColor})`,
            WebkitMaskImage:
              'repeating-linear-gradient(to right, #000 0 2px, transparent 2px 4px)',
            maskImage:
              'repeating-linear-gradient(to right, #000 0 2px, transparent 2px 4px)',
          },
          ...sx,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};
